import { Logger } from 'wdc-cube'

import Divider from '@mui/material/Divider'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Badge,
    Box,
    Button,
    Chip,
    Drawer,
    FormControlLabel,
    Stack,
    Switch,
    Typography
} from '@mui/material'
import { formatDate } from 'date-fns'
import React from 'react'
import { NotificationStyle } from 'src/modules/admin/configurations/scopes'
import { DateFns } from 'src/utils/date-fns'
import { parseLocalDateNoTZ, parseLocalDateTimeNoTZ } from 'src/utils/formatter-utils'
import {
    FCClassContext,
    LifesHubColors,
    LifesHubEmptyState,
    LifesHubRadius,
    ModalClose,
    ModalFooter,
    WorkingBar,
    buttonSx,
    chipSx,
    classToFComponent,
    switchSx
} from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { NotificationsScope } from '../../Main.scopes'

const LOG = Logger.get('NotificationDrawerContent')

const STYLE_COLORS: Record<NotificationStyle, string> = {
    [NotificationStyle.ERROR]: LifesHubColors.danger,
    [NotificationStyle.WARNING]: LifesHubColors.warning,
    [NotificationStyle.SUCCESS]: LifesHubColors.green700,
    [NotificationStyle.INFO]: LifesHubColors.blue
}

const useStyles = makeStyles()((theme) => ({
    drawerPaper: {
        width: 550,
        maxWidth: '100%',
        boxSizing: 'border-box',
        backgroundColor: 'var(--surface)',
        backgroundImage: 'none',
        borderLeft: '1px solid var(--border)',
        color: 'var(--ink)',
        fontSize: 14,
        lineHeight: 1.45
    },

    headerContainer: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(7),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 18
    },

    headerIconWrapper: {
        display: 'grid',
        placeItems: 'center',
        backgroundColor: 'var(--green-100)',
        padding: '4px 8px',
        borderRadius: 8
    },

    headerIcon: {
        color: 'var(--green-800)'
    },

    headerTitle: {
        margin: 0,
        fontSize: 20,
        fontWeight: 700,
        lineHeight: 1.2,
        color: 'var(--ink)'
    },

    headerCaption: {
        display: 'block',
        fontSize: 12,
        color: 'var(--muted)'
    },

    filtersContainer: {
        width: '100%',
        backgroundColor: 'var(--surface-2)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    boxContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8
    },

    width100: {
        width: '100%'
    },

    switchLabel: {
        margin: 0,
        gap: 6,
        '& .MuiFormControlLabel-label': {
            fontSize: 12,
            fontWeight: 700,
            color: '#303733'
        },
        ...switchSx
    },

    tabsContainer: {
        padding: theme.spacing(2),
        overflowX: 'auto',
        whiteSpace: 'nowrap'
    },

    tabBadge: {
        '& .MuiBadge-badge': {
            backgroundColor: 'var(--green-700)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700
        }
    },

    notificationsContainer: {
        flexGrow: 1,
        overflowY: 'auto',
        padding: theme.spacing(1)
    },

    accordion: {
        marginBottom: theme.spacing(1),
        backgroundColor: 'var(--surface)',
        backgroundImage: 'none',
        border: '1px solid var(--border)',
        borderRadius: `${LifesHubRadius.panel} !important`,
        boxShadow: 'var(--shadow-1)',
        '&:before': {
            display: 'none'
        },
        '& .MuiAccordionSummary-root:hover': {
            backgroundColor: '#fafcfb'
        }
    },

    accordionDetails: {
        backgroundColor: '#fbfcfb',
        borderTop: '1px solid var(--border)',
        borderBottomLeftRadius: LifesHubRadius.panel,
        borderBottomRightRadius: LifesHubRadius.panel
    },

    badgeIconWrapper: {
        marginTop: theme.spacing(0.5)
    },

    badgeSpacing: {
        paddingLeft: theme.spacing(1)
    },

    notificationTitle: {
        fontSize: 14,
        fontWeight: 750,
        lineHeight: 1.25,
        color: 'var(--ink)'
    },

    notificationSummary: {
        display: 'block',
        fontSize: 12,
        color: 'var(--muted)'
    },

    notificationDate: {
        marginTop: theme.spacing(1),
        fontSize: 11.5,
        color: 'var(--muted-2)'
    },

    notificationContent: {
        marginBottom: theme.spacing(2),
        fontSize: 13,
        color: 'var(--ink)'
    }
}))

export type NotificationDrawerProps = {
    className?: string
    scope: NotificationsScope
}

class NotificationDrawerClass implements FCClassContext<NotificationDrawerProps> {
    // :: Fields

    scope!: NotificationsScope

    // :: emissors

    readonly emitShowExpiredChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onShowExpiredChange(e.target.checked).catch(LOG.caught)
    readonly emitReadChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onReadChange(e.target.checked).catch(LOG.caught)
    readonly emitNotificationTabChange = (newTab: string) => {
        this.scope.onNotificationTabChange(newTab).catch(LOG.caught)
    }
    readonly emitMarkAsRead = (id: number) => {
        this.scope.onMarkNotificationAsRead(id).catch(LOG.caught)
    }
    readonly emitMarkAllAsRead = () => {
        this.scope.onMarkAllNotificationAsRead().catch(LOG.caught)
    }
    readonly onClose = () => this.scope.onNotificationDrawerToggle().catch(LOG.caught)

    // :: Methods

    getIcon = (type: NotificationStyle) => {
        const sx = { color: this.getColor(type) }

        switch (type) {
            case NotificationStyle.ERROR:
                return <ErrorOutlineIcon sx={sx} />
            case NotificationStyle.WARNING:
                return <WarningAmberIcon sx={sx} />
            case NotificationStyle.SUCCESS:
                return <CheckCircleOutlineIcon sx={sx} />
            default:
                return <InfoOutlinedIcon sx={sx} />
        }
    }

    getColor = (type: NotificationStyle) => STYLE_COLORS[type] ?? LifesHubColors.blue

    onSyncState({ scope: mainScope }: NotificationDrawerProps) {
        this.scope = mainScope
    }

    render() {
        const { scope } = this
        const { classes } = useStyles()

        return (
            <Drawer
                anchor="right"
                open={scope.notificationDrawerOpen}
                onClose={this.onClose}
                classes={{ paper: classes.drawerPaper }}
            >
                <Box className={classes.headerContainer}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            alignItems: 'center'
                        }}
                    >
                        <Box className={classes.headerIconWrapper}>
                            <NotificationsNoneIcon fontSize="large" className={classes.headerIcon} />
                        </Box>
                        <Box>
                            <Typography component="h2" className={classes.headerTitle}>
                                Notificações
                            </Typography>
                            <Typography component="span" className={classes.headerCaption}>
                                {this.scope.unreadMessagesByType['Todas']} PENDENTE
                                {this.scope.unreadMessagesByType['Todas']! > 1 ? 'S' : ''} DE LEITURA
                            </Typography>
                        </Box>
                    </Stack>
                    <ModalClose aria-label="Fechar" onClick={this.onClose}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </ModalClose>
                </Box>

                {this.scope.waiting && <WorkingBar />}

                {this.renderFilters(classes)}
                {this.renderTabs(classes)}
                <Divider sx={{ borderColor: 'var(--border)' }} />
                {this.renderNotifications(classes)}

                <ModalFooter>
                    <Button
                        disabled={!this.scope.unreadMessagesByType['Todas'] || !!this.scope.waiting}
                        startIcon={<DoneAllIcon />}
                        sx={buttonSx()}
                        onClick={() => this.emitMarkAllAsRead()}
                    >
                        Marcar todas como lidas
                    </Button>
                </ModalFooter>
            </Drawer>
        )
    }

    renderFilters(classes: ReturnType<typeof useStyles>['classes']) {
        return (
            <Box className={classes.filtersContainer}>
                {!this.scope.showRead && <Chip size="small" label="Filtrado: Não lidas" sx={chipSx('blue')} />}
                {!this.scope.showExpired && <Chip size="small" label="Filtrado: Não expiradas" sx={chipSx('blue')} />}

                {this.scope.showExpired && this.scope.showRead && (
                    <Chip size="small" label="Todas as notificações" sx={chipSx('green')} />
                )}
                <Box>
                    <FormControlLabel
                        label="Lidas"
                        labelPlacement="start"
                        className={classes.switchLabel}
                        control={
                            <Switch
                                size="small"
                                checked={this.scope.showRead || false}
                                onChange={this.emitReadChange}
                            />
                        }
                    />
                    <FormControlLabel
                        label="Expiradas"
                        labelPlacement="start"
                        className={classes.switchLabel}
                        control={
                            <Switch
                                size="small"
                                checked={this.scope.showExpired || false}
                                onChange={this.emitShowExpiredChange}
                            />
                        }
                    />
                </Box>
            </Box>
        )
    }

    renderTabs(classes: ReturnType<typeof useStyles>['classes']) {
        return (
            <Box className={classes.tabsContainer}>
                <Stack direction="row" spacing={2}>
                    {this.scope.notificationTabs.map((label) => (
                        <Badge
                            key={label}
                            badgeContent={this.scope.unreadMessagesByType[label]}
                            className={classes.tabBadge}
                        >
                            <Button
                                onClick={() => this.emitNotificationTabChange(label)}
                                fullWidth
                                sx={buttonSx(this.scope.notificationTab === label ? 'primary' : 'default', true)}
                            >
                                {label}
                            </Button>
                        </Badge>
                    ))}
                </Stack>
            </Box>
        )
    }

    renderNotifications(classes: ReturnType<typeof useStyles>['classes']) {
        if (!this.scope.filteredNotifications.length) {
            return (
                <Box className={classes.notificationsContainer}>
                    <LifesHubEmptyState
                        title="Nenhuma notificação"
                        text="Não há notificações para os filtros selecionados."
                    />
                </Box>
            )
        }

        return (
            <Box className={classes.notificationsContainer}>
                {this.scope.filteredNotifications.map((notif) => (
                    <Accordion
                        key={notif.id}
                        elevation={0}
                        className={classes.accordion}
                        style={{
                            borderLeft: `4px solid ${this.getColor(notif.notificationStyle)}`
                        }}
                    >
                        <AccordionSummary className={classes.width100} expandIcon={<ExpandMoreIcon />}>
                            <Stack
                                className={classes.width100}
                                direction="row"
                                spacing={2}
                                sx={{
                                    alignItems: 'flex-start'
                                }}
                            >
                                <Badge
                                    color="error"
                                    variant={notif.expired || !!notif.readAt ? 'standard' : 'dot'}
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    className={classes.badgeSpacing}
                                >
                                    <Box className={classes.badgeIconWrapper}>
                                        {this.getIcon(notif.notificationStyle)}
                                    </Box>
                                </Badge>
                                <Box className={classes.width100}>
                                    <Box className={classes.boxContainer}>
                                        <Typography component="div" className={classes.notificationTitle}>
                                            {notif.title}
                                        </Typography>
                                        {notif.type && <Chip size="small" label={notif.type} sx={chipSx('purple')} />}
                                    </Box>
                                    <Typography component="span" className={classes.notificationSummary}>
                                        {notif.summary}
                                    </Typography>
                                    {notif.notificationDate && (
                                        <Stack direction="row" spacing={1}>
                                            <Typography component="div" className={classes.notificationDate}>
                                                {DateFns.formatDate(
                                                    parseLocalDateNoTZ(notif.notificationDate.toString()),
                                                    'dd/MM/yyyy'
                                                )}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>
                            </Stack>
                        </AccordionSummary>

                        <AccordionDetails className={classes.accordionDetails}>
                            <Typography
                                component="div"
                                className={classes.notificationContent}
                                dangerouslySetInnerHTML={{ __html: notif.content }}
                            />

                            {notif.canMarkAsRead && (
                                <Button
                                    disabled={!!notif.readAt}
                                    fullWidth
                                    startIcon={<TaskAltIcon />}
                                    sx={buttonSx('default', true)}
                                    onClick={() => this.emitMarkAsRead(notif.id)}
                                >
                                    {notif.readAt
                                        ? `Lida em: ${formatDate(
                                              parseLocalDateTimeNoTZ(notif.readAt.toString()),
                                              'dd/MM/yyyy HH:mm'
                                          )}`
                                        : 'Marcar como lida'}
                                </Button>
                            )}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        )
    }
}

export default classToFComponent(NotificationDrawerClass)
